'use client';

import type { ReaderAnnotation, ReaderAnnotationMutation } from '@/types/reader-personal';
import { ReaderClientError, readerRequest } from './client';
import { assertReaderIdentity, readerClientIdentity, setReaderClientIdentity } from './client-identity';

export const READER_DRAFT_LIMIT = 200;
export const READER_DRAFT_BYTES = 2 * 1024 * 1024;
export const READER_DRAFT_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
export const READER_DRAFT_EVENT = 'reader-drafts-changed';
const CLEAR_ON_AUTH_KEY = 'reader-drafts-clear-on-auth';
export type ReaderDraft = {
  key: string; userUid: string; mutation: ReaderAnnotationMutation;
  createdAt: number; updatedAt: number; status: 'editing' | 'pending' | 'conflict' | 'blocked'; error: string | null;
};
let database: Promise<IDBDatabase> | null = null;
let channel: BroadcastChannel | null = null;
const replaying = new Map<string, Promise<number>>();

function changed() { if (typeof window !== 'undefined') window.dispatchEvent(new Event(READER_DRAFT_EVENT)); }
function openDatabase() {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('Local draft storage is unavailable. Keep this page open and copy your note before leaving.'));
  database ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('untangled-reader-private-drafts', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('drafts', { keyPath: 'key' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => { database = null; reject(new Error('Local draft storage could not be opened. Copy your note before leaving.')); };
    request.onblocked = () => reject(new Error('Close other reader tabs to update local draft storage.'));
  });
  return database;
}

async function transaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore, result: (value: T) => void, fail: (error: unknown) => void) => void): Promise<T> {
  const db = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction('drafts', mode);
    let value: T;
    tx.oncomplete = () => resolve(value);
    tx.onerror = () => reject(new Error('The local draft could not be saved. Storage may be full; copy your note before leaving.'));
    tx.onabort = () => reject(new Error('The local draft storage operation was interrupted.'));
    try { operation(tx.objectStore('drafts'), (next) => { value = next; }, (error) => { reject(error); tx.abort(); }); }
    catch (error) { tx.abort(); reject(error); }
  });
}

function identityFor(uid: string) {
  const snapshot = readerClientIdentity();
  assertReaderIdentity(snapshot);
  if (snapshot.uid !== uid) throw new Error('This draft belongs to another account.');
  return snapshot;
}

export function validateReaderDraftCapacity(existing: ReaderDraft[], next: ReaderDraft, now = Date.now()) {
  if (!next.mutation.annotationId || !next.mutation.mutationId || next.userUid.length > 128) throw new Error('Invalid local draft identity.');
  const input = next.mutation.input;
  if (input && (input.note.length > 10_000 || (input.anchor?.exact.length ?? 0) > 2_000 || (input.anchor?.prefix.length ?? 0) > 256 || (input.anchor?.suffix.length ?? 0) > 256 || input.tags.length > 10 || input.tags.some((tag) => tag.length > 40))) throw new Error('Notes allow 10,000 characters, 2,000 source characters and up to 10 short tags.');
  const retained = existing.filter((draft) => draft.createdAt >= now - READER_DRAFT_RETENTION_MS && draft.key !== next.key);
  if (retained.some((draft) => draft.mutation.annotationId === next.mutation.annotationId)) throw new Error('Resolve the existing local draft for this item before editing it again.');
  retained.push(next);
  if (retained.length > READER_DRAFT_LIMIT || new TextEncoder().encode(JSON.stringify(retained)).byteLength > READER_DRAFT_BYTES) throw new Error('Local draft storage is full. Export or synchronize your pending drafts before adding another.');
  return retained;
}

export async function activateReaderDraftIdentity(uid: string | null, options: { clearCurrent?: boolean } = {}) {
  setReaderClientIdentity(uid);
  const snapshot = readerClientIdentity();
  if (typeof window === 'undefined') return;
  let pendingClear: string | null = null;
  try { pendingClear = localStorage.getItem(CLEAR_ON_AUTH_KEY); } catch { /* Storage may be unavailable in private browsing. */ }
  try { for (const key of Object.keys(localStorage)) if (key.startsWith('reader-device:') && key !== `reader-device:${uid}`) localStorage.removeItem(key); } catch { /* Device identity is optional when browser storage is restricted. */ }
  if (typeof indexedDB === 'undefined') return;
  if (!channel && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel('untangled-reader-privacy');
    channel.onmessage = (event: MessageEvent<{ clearedUid?: string }>) => {
      if (event.data?.clearedUid && readerClientIdentity().uid === event.data.clearedUid) {
        setReaderClientIdentity(null);
        changed();
      }
    };
  }
  await transaction<void>('readwrite', (store, done) => {
    const request = store.getAll();
    request.onsuccess = () => {
      if (readerClientIdentity().epoch !== snapshot.epoch) return;
      for (const draft of request.result as ReaderDraft[]) {
        if (pendingClear || options.clearCurrent || draft.userUid !== uid || draft.createdAt < Date.now() - READER_DRAFT_RETENTION_MS) store.delete(draft.key);
      }
      done(undefined);
    };
  });
  if (pendingClear && readerClientIdentity().epoch === snapshot.epoch) {
    try { if (localStorage.getItem(CLEAR_ON_AUTH_KEY) === pendingClear) localStorage.removeItem(CLEAR_ON_AUTH_KEY); } catch { /* A repeated cleanup on next auth is safe. */ }
  }
  changed();
}

export async function listReaderDrafts(uid: string): Promise<ReaderDraft[]> {
  const snapshot = identityFor(uid);
  const rows = await transaction<ReaderDraft[]>('readonly', (store, done) => {
    const request = store.getAll();
    request.onsuccess = () => done((request.result as ReaderDraft[]).filter((draft) => draft.userUid === uid && draft.createdAt >= Date.now() - READER_DRAFT_RETENTION_MS).sort((a, b) => a.createdAt - b.createdAt));
  });
  assertReaderIdentity(snapshot);
  return rows;
}

export async function queueReaderMutation(uid: string, mutation: ReaderAnnotationMutation, options: { status?: ReaderDraft['status']; replaceKey?: string } = {}) {
  const snapshot = identityFor(uid);
  const now = Date.now();
  const draft: ReaderDraft = { key: `${uid}:${mutation.mutationId}`, userUid: uid, mutation, createdAt: now, updatedAt: now, status: options.status ?? 'pending', error: null };
  await transaction<void>('readwrite', (store, done, fail) => {
    const request = store.getAll();
    request.onsuccess = () => {
      try {
        assertReaderIdentity(snapshot);
        if (options.replaceKey && !options.replaceKey.startsWith(`${uid}:`)) throw new Error('Invalid draft owner.');
        const existing = (request.result as ReaderDraft[]).filter((row) => row.userUid === uid && row.key !== options.replaceKey);
        validateReaderDraftCapacity(existing, draft, now);
        if (options.replaceKey) store.delete(options.replaceKey);
        for (const expired of existing.filter((row) => row.createdAt < now - READER_DRAFT_RETENTION_MS)) store.delete(expired.key);
        store.put(draft); done(undefined);
      } catch (error) { fail(error); }
    };
  });
  assertReaderIdentity(snapshot);
  changed();
  return draft;
}

export async function discardReaderDraft(uid: string, key: string) {
  const snapshot = identityFor(uid);
  await transaction<void>('readwrite', (store, done) => {
    assertReaderIdentity(snapshot);
    if (!key.startsWith(`${uid}:`)) throw new Error('Invalid draft owner.');
    store.delete(key); done(undefined);
  });
  assertReaderIdentity(snapshot);
  changed();
}

async function markDraft(uid: string, draft: ReaderDraft, status: ReaderDraft['status'], error: string) {
  const snapshot = identityFor(uid);
  await transaction<void>('readwrite', (store, done) => {
    assertReaderIdentity(snapshot);
    const request = store.get(draft.key);
    request.onsuccess = () => { if (request.result) store.put({ ...draft, status, error, updatedAt: Date.now() }); done(undefined); };
  });
  changed();
}

export async function sendReaderMutation(mutation: ReaderAnnotationMutation) {
  const base = '/api/reader/annotations';
  const body = { mutationId: mutation.mutationId, expectedRevision: mutation.expectedRevision, ...((mutation.operation === 'create' || mutation.operation === 'update') ? { input: mutation.input } : {}) };
  if (mutation.operation === 'create') return readerRequest<ReaderAnnotation>(base, { method: 'POST', body: JSON.stringify({ ...body, annotationId: mutation.annotationId }) });
  const path = `${base}/${encodeURIComponent(mutation.annotationId)}${mutation.operation === 'restore' ? '/restore' : ''}`;
  return readerRequest<ReaderAnnotation>(path, { method: mutation.operation === 'update' ? 'PATCH' : mutation.operation === 'delete' ? 'DELETE' : 'POST', body: JSON.stringify(body) });
}

export function replayReaderDrafts(uid: string): Promise<number> {
  const previous = replaying.get(uid);
  if (previous) return previous;
  const snapshot = identityFor(uid);
  const promise = (async () => {
    let acknowledged = 0;
    // Refresh after each pass so a second note saved while the first request is
    // in flight joins the same serial replay. Stop on transport failures.
    for (;;) {
      const drafts = (await listReaderDrafts(uid)).filter((draft) => draft.status === 'pending');
      if (!drafts.length) return acknowledged;
      for (const draft of drafts) {
        assertReaderIdentity(snapshot);
        if (typeof navigator !== 'undefined' && !navigator.onLine) return acknowledged;
        try {
          await sendReaderMutation(draft.mutation);
          assertReaderIdentity(snapshot);
          await discardReaderDraft(uid, draft.key);
          acknowledged++;
        } catch (error) {
          assertReaderIdentity(snapshot);
          if (!(error instanceof ReaderClientError) || error.status >= 500 || error.status === 429) return acknowledged;
          await markDraft(uid, draft, error.status === 409 ? 'conflict' : 'blocked', error.message);
          if (error.status === 401 || error.status === 403) return acknowledged;
        }
      }
    }
  })().finally(() => replaying.delete(uid));
  replaying.set(uid, promise);
  return promise;
}

export async function clearReaderDraftsForSignOut(uid: string) {
  setReaderClientIdentity(null);
  const clearMarker = crypto.randomUUID();
  try { localStorage.removeItem(`reader-device:${uid}`); localStorage.setItem(CLEAR_ON_AUTH_KEY, clearMarker); } catch { /* The UI reports when local storage cleanup cannot be verified. */ }
  channel?.postMessage({ clearedUid: uid });
  changed();
  await transaction<void>('readwrite', (store, done) => {
    const request = store.getAll();
    request.onsuccess = () => { for (const draft of request.result as ReaderDraft[]) if (draft.userUid === uid) store.delete(draft.key); done(undefined); };
  });
  try { if (localStorage.getItem(CLEAR_ON_AUTH_KEY) === clearMarker) localStorage.removeItem(CLEAR_ON_AUTH_KEY); } catch { /* Retain the best-effort retry marker when storage is unavailable. */ }
  changed();
}

export function downloadReaderFile(filename: string, text: string, type = 'application/json') {
  const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }));
  const link = document.createElement('a'); link.href = url; link.download = filename;
  document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function exportReaderDrafts(uid: string) {
  const drafts = await listReaderDrafts(uid);
  // Offline export cannot verify a current source entitlement. Preserve the
  // user's commentary and location metadata without redistributing book text.
  const portable = drafts.map((draft) => ({ ...draft, mutation: { ...draft.mutation, ...(draft.mutation.input ? { input: { ...draft.mutation.input, anchor: draft.mutation.input.anchor ? { ...draft.mutation.input.anchor, exact: '', prefix: '', suffix: '' } : null } } : {}) } }));
  downloadReaderFile('untangled-reader-local-drafts.json', JSON.stringify({ schemaVersion: 1, exportedAt: new Date().toISOString(), notice: 'These drafts are local and may not be synchronized. Your commentary and location metadata are included; protected source excerpts are omitted. Open the original book to attach a recovered passage.', drafts: portable }, null, 2));
}
